import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';
import { putFileToBlobUrl } from '../shared/utils/blob-upload.util';
import { ConfigOrder, ServiceOrder } from '../models/ServiceOrder';

export interface ServiceOrderAttachmentDto {
  id?: number;
  fileName?: string;
  filePath?: string;
  fileType?: string;
  fileSize?: number;
  description?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ServiceOrderAttachmentCreateRequest {
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  description?: string;
  userId?: string;
}

export interface ServiceOrderAttachmentUpdateRequest {
  fileName?: string;
  description?: string;
  userId?: string;
}

export interface ServiceOrderAttachmentUploadUrlResponse {
  uploadUrl: string;
  /** Path/blobName a ser persistido em `filePath` */
  filePath?: string;
  fileName?: string;
}

export interface ServiceOrderExportColumn {
  key: string;
  label: string;
}

export interface ServiceOrderExportRequest {
  columns: string[];
  startDate?: string | null;
  endDate?: string | null;
  status?: string | null;
  configOrderId?: number | null;
  orderNumber?: string | null;
  plate?: string | null;
  configOrderIds?: number[];
}

export interface ServiceOrderExcelExportResult {
  blob: Blob;
  fileName?: string;
}

export interface ServiceOrderDashboardStatusItem {
  status: string;
  count: number;
}

export interface ServiceOrderDashboardData {
  configOrderIds: number[];
  totalInPeriod: number;
  closedLastMonth: number;
  closedLastMonthRange?: { startDate?: string; endDate?: string };
  byStatus: ServiceOrderDashboardStatusItem[];
}

/** Linha do orçamento enviada ao endpoint transacional. */
export interface BudgetFromAppointmentLine {
  description: string;
  quantity: number;
  unitValue: number;
  /** Preventiva | Corretiva. Omitido = Corretiva. */
  maintenanceKind?: string | null;
  /** Percentual cobrado a mais na corretiva. */
  chargePercent?: number | null;
  serviceTypeId?: number | null;
  itemId?: number | null;
  /** Linha de guincho com fornecedor próprio. */
  isGuincho?: boolean;
  externalServiceProviderId?: number | null;
}

export interface BudgetFromAppointmentPayload {
  configOrderId: number;
  departmentId: number;
  providerId?: number | null;
  providerName?: string | null;
  openingReason?: string | null;
  /** Opcionais: quando omitidos, o backend herda do agendamento. */
  vehicleId?: number | null;
  b2bContractId?: number | null;
  plate?: string | null;
  services: BudgetFromAppointmentLine[];
  products: BudgetFromAppointmentLine[];
  /** Data/hora em que o orçamento foi gerado (retroativa). */
  recordedAt?: string | null;
}

export interface BudgetFromAppointmentResult {
  budgetId: number;
  serviceOrderId: number;
  workSectionId: number;
  appointmentId: number;
  osNumber: string;
  status: string;
  totalGross: number;
}

@Injectable({
  providedIn: 'root'
})
export class ServiceOrdersService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'serviceorders';

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private errorService: ErrorService
  ) {}

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;

    if (baseQuery) {
      url += `?${baseQuery}`;
    }

    return url;
  }

  private getHeaders(includeUserId: boolean = false): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    const userId = includeUserId ? this.getAuditUserId() : null;
    if (userId) {
      headerMap['X-User-Id'] = userId;
    }

    return new HttpHeaders(headerMap);
  }

  private getAuditUserId(): string | null {
    const storedUserId = localStorage.getItem('lastLoggedUserId');
    if (storedUserId) {
      return storedUserId;
    }

    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) {
      return null;
    }

    try {
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) {
        return null;
      }

      const normalizedBase64 = payloadBase64
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      const paddedBase64 = normalizedBase64.padEnd(Math.ceil(normalizedBase64.length / 4) * 4, '=');
      const payload = JSON.parse(atob(paddedBase64));
      const userId = payload?.userId ?? payload?.sub ?? payload?.nameid ?? payload?.id;

      return userId != null ? String(userId) : null;
    } catch {
      return null;
    }
  }

  private pickValue(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return undefined;
  }

  private toNumber(value: any): number | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private toBoolean(value: any): boolean | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') return true;
      if (normalized === 'false' || normalized === '0') return false;
      return undefined;
    }

    if (typeof value === 'number') {
      return value !== 0;
    }

    return Boolean(value);
  }

  private pickNestedNumber(source: any, paths: string[][]): number | undefined {
    for (const path of paths) {
      let current = source;
      for (const key of path) {
        current = current?.[key];
        if (current === undefined || current === null) {
          break;
        }
      }

      const parsed = this.toNumber(current);
      if (parsed !== undefined) {
        return parsed;
      }
    }

    return undefined;
  }

  private normalizeArray<T>(value: T[] | null | undefined): T[] {
    return Array.isArray(value) ? value : [];
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.Data)) return response.Data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.Items)) return response.Items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.data?.Items)) return response.data.Items;
    if (Array.isArray(response?.serviceOrders)) return response.serviceOrders;
    if (Array.isArray(response?.ServiceOrders)) return response.ServiceOrders;
    if (Array.isArray(response?.data?.serviceOrders)) return response.data.serviceOrders;
    if (Array.isArray(response?.data?.ServiceOrders)) return response.data.ServiceOrders;
    if (Array.isArray(response?.results)) return response.results;
    if (Array.isArray(response?.Results)) return response.Results;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.serviceOrder ?? response?.ServiceOrder ?? response;
  }

  private mapExportColumn(dto: any): ServiceOrderExportColumn | null {
    const key = this.pickValue(dto, ['key', 'Key', 'name', 'Name', 'column', 'Column', 'field', 'Field']);
    if (!key) {
      return null;
    }

    const label = this.pickValue(dto, ['label', 'Label', 'title', 'Title', 'displayName', 'DisplayName']) ?? String(key);
    return { key: String(key), label: String(label) };
  }

  private extractExportColumns(response: any): ServiceOrderExportColumn[] {
    const rawList = this.extractArray(response);
    const nestedColumns = this.pickValue(response, ['columns', 'Columns']);
    const list = rawList.length > 0
      ? rawList
      : Array.isArray(nestedColumns)
        ? nestedColumns
        : [];

    return list
      .map(item => this.mapExportColumn(item))
      .filter((item): item is ServiceOrderExportColumn => !!item);
  }

  private buildExportPayload(body: ServiceOrderExportRequest): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      columns: body.columns
    };

    if (body.startDate) payload['startDate'] = body.startDate;
    if (body.endDate) payload['endDate'] = body.endDate;
    if (body.status?.trim()) payload['status'] = body.status.trim();
    if (body.orderNumber?.trim()) payload['orderNumber'] = body.orderNumber.trim();
    if (body.plate?.trim()) payload['plate'] = body.plate.trim();
    if (body.configOrderId != null && body.configOrderId > 0) payload['configOrderId'] = body.configOrderId;
    if (body.configOrderIds?.length) payload['configOrderIds'] = body.configOrderIds;

    return payload;
  }

  private extractFileNameFromContentDisposition(header: string | null): string | undefined {
    if (!header) {
      return undefined;
    }

    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
    }

    const asciiMatch = header.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1]?.trim() || undefined;
  }

  private mapExcelExportResponse(response: { body: Blob | null; headers: { get(name: string): string | null } }): ServiceOrderExcelExportResult {
    const blob = response.body;
    if (!blob) {
      throw new Error('Resposta vazia do servidor');
    }

    let excelBlob = blob;
    if (!blob.type || !blob.type.includes('spreadsheet')) {
      excelBlob = new Blob([blob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    }

    const fileName = this.extractFileNameFromContentDisposition(
      response.headers.get('content-disposition')
    );

    return { blob: excelBlob, fileName };
  }

  // ===== ServiceOrderAttachments (novos endpoints) =====

  private normalizeUrlCandidate(value: any): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : undefined;
    }
    return undefined;
  }

  private normalizeUploadUrlResponse(res: any): ServiceOrderAttachmentUploadUrlResponse {
    const data = res?.data ?? res?.Data ?? res?.result ?? res?.Result;

    const rawUploadUrl =
      this.normalizeUrlCandidate(res?.uploadUrl) ??
      this.normalizeUrlCandidate(res?.sasUrl) ??
      this.normalizeUrlCandidate(res?.url) ??
      this.normalizeUrlCandidate(res?.UploadUrl) ??
      this.normalizeUrlCandidate(res?.SasUrl) ??
      this.normalizeUrlCandidate(res?.Url) ??
      this.normalizeUrlCandidate(data?.uploadUrl) ??
      this.normalizeUrlCandidate(data?.sasUrl) ??
      this.normalizeUrlCandidate(data?.url) ??
      this.normalizeUrlCandidate(data?.UploadUrl) ??
      this.normalizeUrlCandidate(data?.SasUrl) ??
      this.normalizeUrlCandidate(data?.Url);

    if (!rawUploadUrl) {
      throw new Error('Resposta inválida ao gerar URL de upload');
    }

    const filePath =
      this.normalizeUrlCandidate(res?.filePath) ??
      this.normalizeUrlCandidate(res?.blobName) ??
      this.normalizeUrlCandidate(res?.BlobName) ??
      this.normalizeUrlCandidate(res?.FilePath) ??
      this.normalizeUrlCandidate(data?.filePath) ??
      this.normalizeUrlCandidate(data?.FilePath) ??
      this.normalizeUrlCandidate(data?.blobName) ??
      this.normalizeUrlCandidate(data?.BlobName);

    const fileName =
      this.normalizeUrlCandidate(res?.fileName) ??
      this.normalizeUrlCandidate(res?.FileName) ??
      this.normalizeUrlCandidate(data?.fileName) ??
      this.normalizeUrlCandidate(data?.FileName);

    return {
      uploadUrl: rawUploadUrl,
      filePath,
      fileName
    };
  }

  private normalizeDownloadUrlResponse(res: any): string {
    const data = res?.data ?? res?.Data ?? res?.result ?? res?.Result;
    const url =
      this.normalizeUrlCandidate(res?.downloadUrl) ??
      this.normalizeUrlCandidate(res?.url) ??
      this.normalizeUrlCandidate(res?.DownloadUrl) ??
      this.normalizeUrlCandidate(res?.Url) ??
      this.normalizeUrlCandidate(data?.downloadUrl) ??
      this.normalizeUrlCandidate(data?.url) ??
      this.normalizeUrlCandidate(data?.DownloadUrl) ??
      this.normalizeUrlCandidate(data?.Url);

    if (url) return url;
    if (typeof res === 'string' && res.trim()) return res.trim();
    throw new Error('Resposta inválida ao gerar URL de download');
  }

  /** GET /serviceorders/{id}/attachments/upload-url?fileName=... */
  getAttachmentUploadUrl(serviceOrderId: number, fileName: string): Observable<ServiceOrderAttachmentUploadUrlResponse> {
    const params = new HttpParams().set('fileName', fileName);

    return this.http
      .get<any>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments/upload-url`), {
        headers: this.getHeaders(),
        params
      })
      .pipe(
        map(res => this.normalizeUploadUrlResponse(res)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao gerar URL de upload do anexo');
          return throwError(() => err);
        })
      );
  }

  /** Upload direto para a URL SAS retornada pelo backend (Azure Blob). */
  uploadAttachmentToUrl(uploadUrl: string, file: File): Observable<void> {
    return putFileToBlobUrl(this.httpBackend, uploadUrl, file).pipe(
      catchError(err => {
        this.errorService.showErrorMessage(err?.message || 'Erro ao enviar arquivo do anexo');
        return throwError(() => err);
      }),
    );
  }

  /** POST /serviceorders/{id}/attachments */
  createAttachment(serviceOrderId: number, payload: ServiceOrderAttachmentCreateRequest): Observable<ServiceOrderAttachmentDto> {
    return this.http
      .post<any>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments`), payload, { headers: this.getHeaders() })
      .pipe(
        map(res => this.extractItem(res) as ServiceOrderAttachmentDto),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao criar anexo');
          return throwError(() => err);
        })
      );
  }

  /** GET /serviceorders/{id}/attachments */
  listAttachments(serviceOrderId: number): Observable<ServiceOrderAttachmentDto[]> {
    return this.http
      .get<any>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments`), { headers: this.getHeaders() })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.extractItem(dto) as ServiceOrderAttachmentDto)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar anexos da OS');
          return throwError(() => err);
        })
      );
  }

  /** GET /serviceorders/{id}/attachments/{attachmentId}/download-url */
  getAttachmentDownloadUrlById(serviceOrderId: number, attachmentId: number): Observable<string> {
    return this.http
      .get<any>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments/${attachmentId}/download-url`), {
        headers: this.getHeaders()
      })
      .pipe(
        map(res => this.normalizeDownloadUrlResponse(res)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao gerar URL de download do anexo');
          return throwError(() => err);
        })
      );
  }

  /**
   * @deprecated O endpoint por blobName foi removido na refatoração. Prefira `getAttachmentDownloadUrlById`.
   * Mantido por compatibilidade; irá falhar se chamado contra a API atual.
   */
  getAttachmentDownloadUrlByBlobName(serviceOrderId: number, blobName: string): Observable<string> {
    const params = new HttpParams().set('blobName', blobName);
    return this.http
      .get<any>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments/download-url`), {
        headers: this.getHeaders(),
        params
      })
      .pipe(
        map(res => this.normalizeDownloadUrlResponse(res)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao gerar URL de download do anexo');
          return throwError(() => err);
        })
      );
  }

  /** PUT /serviceorders/{id}/attachments/{attachmentId} */
  updateAttachment(serviceOrderId: number, attachmentId: number, payload: ServiceOrderAttachmentUpdateRequest): Observable<ServiceOrderAttachmentDto> {
    return this.http
      .put<any>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments/${attachmentId}`), payload, { headers: this.getHeaders() })
      .pipe(
        map(res => this.extractItem(res) as ServiceOrderAttachmentDto),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao atualizar anexo');
          return throwError(() => err);
        })
      );
  }

  /** DELETE /serviceorders/{id}/attachments/{attachmentId}?userId=... */
  deleteAttachment(serviceOrderId: number, attachmentId: number, userId?: string): Observable<void> {
    let params = new HttpParams();
    if (userId) {
      params = params.set('userId', userId);
    }

    return this.http
      .delete<void>(this.buildUrl(`${this.resourcePath}/${serviceOrderId}/attachments/${attachmentId}`), {
        headers: this.getHeaders(),
        params
      })
      .pipe(
        map(() => void 0),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao remover anexo');
          return throwError(() => err);
        })
      );
  }

  /** Fluxo completo: gera URL SAS, faz upload e cria o registro do anexo. */
  uploadAndRegisterAttachment(
    serviceOrderId: number,
    file: File,
    meta?: { type?: string; description?: string; userId?: string }
  ): Observable<ServiceOrderAttachmentDto> {
    const safeFileName = file?.name ? String(file.name) : `anexo_${Date.now()}`;
    const normalizedUserId = meta?.userId ?? this.getAuditUserId() ?? undefined;

    return this.getAttachmentUploadUrl(serviceOrderId, safeFileName).pipe(
      switchMap((uploadRes) =>
        this.uploadAttachmentToUrl(uploadRes.uploadUrl, file).pipe(
          map(() => uploadRes)
        )
      ),
      switchMap((uploadRes) => {
        // Preferir exatamente o caminho retornado pelo backend (normalmente blobName).
        const filePath = uploadRes.filePath ?? `serviceorders/${serviceOrderId}/${uploadRes.fileName ?? safeFileName}`;

        // Persistimos o tipo dentro da description, pois o endpoint não possui campo próprio.
        const trimmedType = (meta?.type ?? '').toString().trim();
        const trimmedDesc = (meta?.description ?? '').toString().trim();
        const description = trimmedType
          ? `[${trimmedType}]${trimmedDesc ? ' ' + trimmedDesc : ''}`
          : (trimmedDesc || undefined);

        const payload: ServiceOrderAttachmentCreateRequest = {
          fileName: uploadRes.fileName ?? safeFileName,
          filePath,
          fileType: file.type || 'application/octet-stream',
          fileSize: file.size,
          description,
          userId: normalizedUserId
        };

        return this.createAttachment(serviceOrderId, payload);
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao enviar anexo');
        return throwError(() => err);
      })
    );
  }

  private mapConfigOrder(dto: any): ConfigOrder | undefined {
    if (!dto) return undefined;
    return {
      id: this.toNumber(dto.Id ?? dto.id),
      typeOS: dto.TypeOS ?? dto.typeOS,
      groupId: this.toNumber(dto.GroupId ?? dto.groupId),
      departmentId: this.toNumber(dto.DepartmentId ?? dto.departmentId),
      statusClosedServiceOrder:
        dto.StatusClosedServiceOrder ??
        dto.statusClosedServiceOrder ??
        dto.StatusClosed ??
        dto.statusClosed ??
        null,
      transaction: dto.Transaction ?? dto.transaction ?? false,
      isThirdParty: this.normalizeBoolean(
        dto.CanThirdParty ??
          dto.canThirdParty ??
          dto.IsThirdParty ??
          dto.isThirdParty
      ),
      canVehicle: dto.CanVehicle ?? dto.canVehicle ?? true,
      canAditionalVehicle: dto.CanAditionalVehicle ?? dto.canAditionalVehicle ?? false,
      canClient: dto.CanClient ?? dto.canClient ?? true,
      canStock: dto.CanStock ?? dto.canStock ?? true,
      canServices: dto.CanServices ?? dto.canServices ?? true,
      canClose: dto.CanClose ?? dto.canClose ?? true,
      active: dto.Active ?? dto.active ?? true,
    };
  }

  private normalizeBoolean(value: any): boolean | undefined {
    if (value == null) return undefined;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'true' || v === '1' || v === 'sim' || v === 'yes') return true;
      if (v === 'false' || v === '0' || v === 'nao' || v === 'não' || v === 'no') return false;
    }
    return undefined;
  }

  private mapFromDto(dto: any): ServiceOrder {
    const openingDate = this.pickValue(dto, ['dataAbertura', 'DataAbertura', 'openingDate', 'OpeningDate']);

    // Resolve nested objects from API response
    const vehicle = dto?.Vehicle ?? dto?.vehicle;
    const customer = dto?.Customer ?? dto?.customer;
    const staff = dto?.Staff ?? dto?.staff;
    const stock = dto?.Stock ?? dto?.stock;

    return {
      ...dto,
      id: this.toNumber(this.pickValue(dto, ['id', 'Id'])),
      numeroOS: this.pickValue(dto, ['numeroOS', 'NumeroOS', 'orderNumber', 'OrderNumber']) ?? '',
      configOrderId: this.toNumber(this.pickValue(dto, ['configOrderId', 'ConfigOrderId'])),
      configOrder: this.mapConfigOrder(this.pickValue(dto, ['configOrder', 'ConfigOrder'])),
      stockId: this.toNumber(this.pickValue(dto, ['stockId', 'StockId'])),
      usuarioId: this.toNumber(this.pickValue(dto, ['usuarioId', 'UsuarioId', 'userId', 'UserId'])),
      usuarioNome: this.pickValue(dto, ['usuarioNome', 'UsuarioNome', 'userName', 'UserName']) ?? '',
      unidadeId: this.toNumber(this.pickValue(dto, ['unidadeId', 'UnidadeId', 'departmentId', 'DepartmentId'])),
      unidadeNome: this.pickValue(dto, ['unidadeNome', 'UnidadeNome', 'departmentName', 'DepartmentName']) ?? '',
      dataAbertura: openingDate ?? '',
      horaAbertura: this.pickValue(dto, ['horaAbertura', 'HoraAbertura']) ?? this.extractOpeningHour(dto, openingDate),
      dataPrevistaConclusao: this.pickValue(dto, ['dataPrevistaConclusao', 'DataPrevistaConclusao', 'expectedCompletionDate', 'ExpectedCompletionDate']),
      dataConclusao: this.pickValue(dto, ['dataConclusao', 'DataConclusao', 'closingDate', 'ClosingDate']),
      setorAbertura: this.pickValue(dto, ['setorAbertura', 'SetorAbertura']) ?? '',
      tipoOS: this.pickValue(dto, ['tipoOS', 'TipoOS', 'typeOS', 'TypeOS']),
      tipo: this.pickValue(dto, ['tipo', 'Tipo', 'type', 'Type']) ?? 'Outros',
      motivoAbertura: this.extractOpeningReason(dto) ?? '',
      status: this.pickValue(dto, ['status', 'Status']) ?? 'Aberta',
      veiculoId: this.toNumber(this.pickValue(dto, ['veiculoId', 'VeiculoId', 'vehicleId', 'VehicleId'])),
      veiculoPlaca: this.pickValue(dto, ['veiculoPlaca', 'VeiculoPlaca', 'vehiclePlate', 'VehiclePlate'])
        ?? (vehicle ? `${vehicle.PlateNumber ?? vehicle.plateNumber ?? ''}` : undefined),
      // KM do cadastro do veículo tem prioridade: o openingMileage é o KM de abertura da
      // OS e, usado como fallback, fazia o campo "KM Atual" mostrar sempre o valor errado.
      km: this.toNumber(
        vehicle?.CurrentMileage
        ?? vehicle?.currentMileage
        ?? this.pickValue(dto, ['km', 'Km', 'mileage', 'Mileage', 'openingMileage', 'OpeningMileage'])
      ),
      combustivel: this.pickValue(dto, ['combustivel', 'Combustivel', 'fuel', 'Fuel', 'fuelLevel', 'FuelLevel']),
      veiculoAdicionalId: this.toNumber(this.pickValue(dto, ['veiculoAdicionalId', 'VeiculoAdicionalId', 'additionalVehicleId', 'AdditionalVehicleId'])),
      veiculoAdicionalPlaca: this.pickValue(dto, ['veiculoAdicionalPlaca', 'VeiculoAdicionalPlaca', 'additionalVehiclePlate', 'AdditionalVehiclePlate']),
      motivoVeiculoAdicional: this.pickValue(dto, ['motivoVeiculoAdicional', 'MotivoVeiculoAdicional', 'additionalVehicleReason', 'AdditionalVehicleReason']),
      clienteId: this.toNumber(this.pickValue(dto, ['clienteId', 'ClienteId', 'customerId', 'CustomerId'])),
      clienteNome: this.pickValue(dto, ['clienteNome', 'ClienteNome', 'customerName', 'CustomerName'])
        ?? (customer?.Name ?? customer?.name ?? customer?.NomeFantasia ?? customer?.nomeFantasia ?? customer?.RazaoSocial ?? customer?.razaoSocial),
      contratoId: this.toNumber(this.pickValue(dto, ['contratoId', 'ContratoId', 'contractId', 'ContractId'])),
      contratoNumero: this.pickValue(dto, ['contratoNumero', 'ContratoNumero', 'contractNumber', 'ContractNumber']),
      b2bContractId: this.toNumber(this.pickValue(dto, ['b2bContractId', 'B2BContractId', 'b2BContractId'])),
      b2bContractItemId: this.toNumber(this.pickValue(dto, ['b2bContractItemId', 'B2BContractItemId', 'b2BContractItemId'])),
      fuelLevel: this.pickValue(dto, ['fuelLevel', 'FuelLevel', 'combustivel', 'Combustivel', 'fuel', 'Fuel']),
      openingMileage: this.toNumber(this.pickValue(dto, ['openingMileage', 'OpeningMileage', 'km', 'Km', 'mileage', 'Mileage'])),
      entryDate: this.pickValue(dto, ['entryDate', 'EntryDate']),
      startWorkDate: this.pickValue(dto, ['startWorkDate', 'StartWorkDate']),
      deadlineDate: this.pickValue(dto, ['deadlineDate', 'DeadlineDate', 'dataPrevistaConclusao', 'DataPrevistaConclusao']),
      endWorkDate: this.pickValue(dto, ['endWorkDate', 'EndWorkDate']),
      pickupDate: this.pickValue(dto, ['pickupDate', 'PickupDate']),
      isScheduled: this.toBoolean(this.pickValue(dto, ['isScheduled', 'IsScheduled'])) ?? false,
      mecanicoId: this.toNumber(this.pickValue(dto, ['mecanicoId', 'MecanicoId', 'staffId', 'StaffId'])),
      mecanicoNome: this.pickValue(dto, ['mecanicoNome', 'MecanicoNome', 'staffName', 'StaffName'])
        ?? (staff?.Name ?? staff?.name ?? staff?.NomeCompleto ?? staff?.nomeCompleto),
      almoxarifadoId: this.toNumber(this.pickValue(dto, ['almoxarifadoId', 'AlmoxarifadoId', 'warehouseId', 'WarehouseId'])),
      almoxarifadoNome: this.pickValue(dto, ['almoxarifadoNome', 'AlmoxarifadoNome', 'warehouseName', 'WarehouseName'])
        ?? (stock?.Name ?? stock?.name),
      descricao: this.pickValue(dto, ['descricao', 'Descricao', 'description', 'Description']) ?? '',
      valorEstimado: this.toNumber(this.pickValue(dto, ['valorEstimado', 'ValorEstimado', 'estimatedValue', 'EstimatedValue'])),
      valorFinal: this.toNumber(this.pickValue(dto, ['valorFinal', 'ValorFinal', 'finalValue', 'FinalValue'])),
      observacoes: this.normalizeArray(this.pickValue(dto, ['observacoes', 'Observacoes'])),
      historico: this.normalizeArray(
        this.pickValue(dto, ['historico', 'Historico', 'history', 'History', 'movements', 'Movements'])
      ),
      servicos: this.normalizeArray(this.pickValue(dto, ['servicos', 'Servicos', 'services', 'Services'])),
      anexos: this.normalizeArray(
        this.pickValue(dto, ['anexos', 'Anexos', 'attachments', 'Attachments'])
      ),
      checklists: this.normalizeArray(this.pickValue(dto, ['checklists', 'Checklists'])),
      createdAt: this.pickValue(dto, ['createdAt', 'CreatedAt']),
      updatedAt: this.pickValue(dto, ['updatedAt', 'UpdatedAt'])
    } as ServiceOrder;
  }

  /** Retorna o motivo de abertura somente quando a API envia o campo explicitamente. */
  private extractOpeningReason(dto: any): string | undefined {
    const value = this.pickValue(dto, [
      'motivoAbertura',
      'MotivoAbertura',
      'openingReason',
      'OpeningReason',
      'reason',
      'Reason'
    ]);

    if (value === undefined || value === null) {
      return undefined;
    }

    const normalized = String(value).trim();
    return normalized || undefined;
  }

  private extractTime(dateValue: string | null | undefined): string {
    if (!dateValue) {
      return '';
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  /**
   * Extrai a hora de abertura da OS.
   * Tenta OpeningDate primeiro; se for meia-noite (placeholder do backend),
   * usa CreatedAt que sempre contém a hora real de criação.
   */
  private extractOpeningHour(dto: any, openingDate: any): string {
    const fromOpening = this.extractTime(openingDate);
    if (fromOpening && fromOpening !== '00:00') {
      return fromOpening;
    }

    const createdAt = this.pickValue(dto, ['createdAt', 'CreatedAt']);
    const fromCreated = this.extractTime(createdAt);
    if (fromCreated && fromCreated !== '00:00') {
      return fromCreated;
    }

    return fromOpening || '';
  }

  getAll(): Observable<ServiceOrder[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar ordens de serviço');
        return throwError(() => err);
        })
      );
  }

  getPaginated(
    pageNumber: number = 1,
    pageSize: number = 20,
    filters?: {
      status?: string;
      orderNumber?: string;
      plate?: string;
      startDate?: string;
      endDate?: string;
      configOrderId?: number;
      customerId?: number;
      vehicleId?: number;
      staffId?: number;
      departmentId?: number;
      b2bContractId?: number;
      b2bContractItemId?: number;
      isScheduled?: boolean;
    }
  ): Observable<{ serviceOrders: ServiceOrder[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (filters?.status?.trim()) params = params.set('status', filters.status.trim());
    if (filters?.orderNumber?.trim()) params = params.set('orderNumber', filters.orderNumber.trim());
    if (filters?.plate?.trim()) params = params.set('plate', filters.plate.trim());
    if (filters?.startDate) params = params.set('startDate', filters.startDate);
    if (filters?.endDate) params = params.set('endDate', filters.endDate);
    
    if (filters?.configOrderId && filters.configOrderId > 0) {
      params = params.set('configOrderId', filters.configOrderId.toString());
    }
    
    if (filters?.customerId) params = params.set('customerId', filters.customerId.toString());
    if (filters?.vehicleId) params = params.set('vehicleId', filters.vehicleId.toString());
    if (filters?.staffId) params = params.set('staffId', filters.staffId.toString());
    if (filters?.departmentId) params = params.set('departmentId', filters.departmentId.toString());
    if (filters?.b2bContractId) params = params.set('b2bContractId', filters.b2bContractId.toString());
    if (filters?.b2bContractItemId) params = params.set('b2bContractItemId', filters.b2bContractItemId.toString());
    if (filters?.isScheduled !== undefined && filters?.isScheduled !== null) {
      params = params.set('isScheduled', String(filters.isScheduled));
    }

    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => {
          console.log('[ServiceOrders] getPaginated response:', res);
          const list = this.extractArray(res);
          const serviceOrders = list.map(dto => this.mapFromDto(dto));
          const totalCount = this.pickNestedNumber(res, [
            ['totalCount'], ['TotalCount'], ['total'], ['Total'],
            ['totalItems'], ['TotalItems'], ['totalRecords'], ['TotalRecords'],
            ['data', 'totalCount'], ['data', 'TotalCount'], ['data', 'total'], ['data', 'Total'],
            ['data', 'totalItems'], ['data', 'TotalItems'],
            ['Data', 'totalCount'], ['Data', 'TotalCount'], ['Data', 'totalItems'], ['Data', 'TotalItems'],
            ['pagination', 'totalCount'], ['pagination', 'TotalCount'],
            ['pagination', 'totalItems'], ['pagination', 'TotalItems'],
            ['data', 'pagination', 'totalCount'], ['data', 'pagination', 'TotalCount'],
            ['data', 'pagination', 'totalItems'], ['data', 'pagination', 'TotalItems'],
            ['Data', 'pagination', 'totalCount'], ['Data', 'pagination', 'TotalCount'],
            ['Data', 'pagination', 'totalItems'], ['Data', 'pagination', 'TotalItems'],
            ['Pagination', 'totalCount'], ['Pagination', 'TotalCount'],
            ['Pagination', 'totalItems'], ['Pagination', 'TotalItems'],
            ['data', 'Pagination', 'totalCount'], ['data', 'Pagination', 'TotalCount'],
            ['data', 'Pagination', 'totalItems'], ['data', 'Pagination', 'TotalItems'],
            ['Data', 'Pagination', 'totalCount'], ['Data', 'Pagination', 'TotalCount'],
            ['Data', 'Pagination', 'totalItems'], ['Data', 'Pagination', 'TotalItems'],
            ['meta', 'totalCount'], ['meta', 'TotalCount'],
            ['meta', 'totalItems'], ['meta', 'TotalItems']
          ]) ?? serviceOrders.length;

          const currentPage = this.pickNestedNumber(res, [
            ['page'], ['Page'], ['pageNumber'], ['PageNumber'],
            ['data', 'page'], ['data', 'Page'], ['data', 'pageNumber'], ['data', 'PageNumber'],
            ['Data', 'page'], ['Data', 'Page'], ['Data', 'pageNumber'], ['Data', 'PageNumber'],
            ['pagination', 'page'], ['pagination', 'Page'],
            ['data', 'pagination', 'page'], ['data', 'pagination', 'Page'],
            ['Data', 'pagination', 'page'], ['Data', 'pagination', 'Page'],
            ['Pagination', 'page'], ['Pagination', 'Page'],
            ['Pagination', 'pageNumber'], ['Pagination', 'PageNumber'],
            ['data', 'Pagination', 'page'], ['data', 'Pagination', 'Page'],
            ['data', 'Pagination', 'pageNumber'], ['data', 'Pagination', 'PageNumber'],
            ['Data', 'Pagination', 'page'], ['Data', 'Pagination', 'Page'],
            ['Data', 'Pagination', 'pageNumber'], ['Data', 'Pagination', 'PageNumber'],
            ['meta', 'page'], ['meta', 'Page']
          ]) ?? pageNumber;

          const size = this.pickNestedNumber(res, [
            ['pageSize'], ['PageSize'],
            ['data', 'pageSize'], ['data', 'PageSize'],
            ['Data', 'pageSize'], ['Data', 'PageSize'],
            ['pagination', 'pageSize'], ['pagination', 'PageSize'],
            ['data', 'pagination', 'pageSize'], ['data', 'pagination', 'PageSize'],
            ['Data', 'pagination', 'pageSize'], ['Data', 'pagination', 'PageSize'],
            ['Pagination', 'pageSize'], ['Pagination', 'PageSize'],
            ['data', 'Pagination', 'pageSize'], ['data', 'Pagination', 'PageSize'],
            ['Data', 'Pagination', 'pageSize'], ['Data', 'Pagination', 'PageSize'],
            ['meta', 'pageSize'], ['meta', 'PageSize']
          ]) ?? pageSize;

          const totalPages = this.pickNestedNumber(res, [
            ['totalPages'], ['TotalPages'],
            ['data', 'totalPages'], ['data', 'TotalPages'],
            ['Data', 'totalPages'], ['Data', 'TotalPages'],
            ['pagination', 'totalPages'], ['pagination', 'TotalPages'],
            ['data', 'pagination', 'totalPages'], ['data', 'pagination', 'TotalPages'],
            ['Data', 'pagination', 'totalPages'], ['Data', 'pagination', 'TotalPages'],
            ['Pagination', 'totalPages'], ['Pagination', 'TotalPages'],
            ['data', 'Pagination', 'totalPages'], ['data', 'Pagination', 'TotalPages'],
            ['Data', 'Pagination', 'totalPages'], ['Data', 'Pagination', 'TotalPages'],
            ['meta', 'totalPages'], ['meta', 'TotalPages']
          ]) ?? Math.max(1, Math.ceil(totalCount / size));

          return { serviceOrders, totalCount, page: currentPage, pageSize: size, totalPages };
        }),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço');
          return throwError(() => err);
        })
      );
  }

  getById(id: number): Observable<ServiceOrder> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() })
      .pipe(
        map(res => this.mapFromDto(this.extractItem(res))),
        catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar ordem de serviço');
        return throwError(() => err);
        })
      );
  }

  getByOrderNumber(orderNumber: string): Observable<ServiceOrder> {
    const params = new HttpParams().set('orderNumber', orderNumber);

    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => {
          // Endpoint pode retornar uma lista ou um item único em formato de query.
          const list = this.extractArray(res);
          if (list.length > 0) {
            return this.mapFromDto(list[0]);
          }
          return this.mapFromDto(this.extractItem(res));
        }),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordem de serviço por número');
          return throwError(() => err);
        })
      );
  }

  getByConfigOrderId(configOrderId: number): Observable<ServiceOrder[]> {
    const params = new HttpParams().set('configOrderId', String(configOrderId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço por configuração');
          return throwError(() => err);
        })
      );
  }

  getByCustomerId(customerId: number): Observable<ServiceOrder[]> {
    const params = new HttpParams().set('customerId', String(customerId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço por cliente');
          return throwError(() => err);
        })
      );
  }

  getByVehicleId(vehicleId: number): Observable<ServiceOrder[]> {
    const params = new HttpParams().set('vehicleId', String(vehicleId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço por veículo');
          return throwError(() => err);
        })
      );
  }

  getByStaffId(staffId: number): Observable<ServiceOrder[]> {
    const params = new HttpParams().set('staffId', String(staffId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço por responsável');
          return throwError(() => err);
        })
      );
  }

  getOpen(): Observable<ServiceOrder[]> {
    const params = new HttpParams().set('status', 'Aberto');
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço abertas');
          return throwError(() => err);
        })
      );
  }

  getClosed(): Observable<ServiceOrder[]> {
    const params = new HttpParams().set('status', 'Fechada');
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço fechadas');
          return throwError(() => err);
        })
      );
  }

  getByDateRange(startDate: string, endDate: string): Observable<ServiceOrder[]> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar ordens de serviço por período');
          return throwError(() => err);
        })
      );
  }

  create(serviceOrder: ServiceOrder): Observable<ServiceOrder> {
    return this.http.post<any>(this.buildUrl(this.resourcePath), serviceOrder, { headers: this.getHeaders(true) })
      .pipe(
        map(res => this.mapFromDto(this.extractItem(res))),
        catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar ordem de serviço');
        return throwError(() => err);
        })
      );
  }

  /**
   * POST /appointments/{id}/budget — cria OS + seção + linhas + orçamento e vincula
   * ao agendamento em uma única transação no backend.
   */
  createBudgetFromAppointment(
    appointmentId: number,
    payload: BudgetFromAppointmentPayload
  ): Observable<BudgetFromAppointmentResult> {
    return this.http
      .post<any>(
        this.buildUrl(`appointments/${appointmentId}/budget`),
        payload,
        { headers: this.getHeaders(true) }
      )
      .pipe(
        map(res => this.mapBudgetFromAppointmentResult(this.extractItem(res))),
        catchError(err => {
          this.errorService.showErrorMessage(
            err?.error?.message || 'Erro ao criar orçamento a partir do agendamento'
          );
          return throwError(() => err);
        })
      );
  }

  private mapBudgetFromAppointmentResult(dto: any): BudgetFromAppointmentResult {
    return {
      budgetId: Number(this.pickValue(dto, ['budgetId', 'BudgetId']) || 0),
      serviceOrderId: Number(this.pickValue(dto, ['serviceOrderId', 'ServiceOrderId']) || 0),
      workSectionId: Number(this.pickValue(dto, ['workSectionId', 'WorkSectionId']) || 0),
      appointmentId: Number(this.pickValue(dto, ['appointmentId', 'AppointmentId']) || 0),
      osNumber: String(this.pickValue(dto, ['osNumber', 'OsNumber', 'OSNumber']) || ''),
      status: String(this.pickValue(dto, ['status', 'Status']) || ''),
      totalGross: Number(this.pickValue(dto, ['totalGross', 'TotalGross']) || 0)
    };
  }

  update(id: number, serviceOrder: ServiceOrder): Observable<ServiceOrder> {
    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), serviceOrder, { headers: this.getHeaders(true) })
      .pipe(
        map(res => this.mapFromDto(this.extractItem(res))),
        catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar ordem de serviço');
        return throwError(() => err);
        })
      );
  }

  /**
   * POST /serviceorders/{id}/cancel-linked-budgets
   * Cancela orçamentos abertos vinculados à OS (sem alterar o status da OS).
   */
  cancelLinkedBudgets(serviceOrderId: number): Observable<{ cancelled: number; skipped: number; total: number }> {
    return this.http
      .post<any>(
        this.buildUrl(`${this.resourcePath}/${serviceOrderId}/cancel-linked-budgets`),
        {},
        { headers: this.getHeaders(true) }
      )
      .pipe(
        map(res => {
          const data = this.extractItem(res) || {};
          return {
            cancelled: Number(data.cancelled ?? data.Cancelled ?? 0),
            skipped: Number(data.skipped ?? data.Skipped ?? 0),
            total: Number(data.total ?? data.Total ?? 0)
          };
        }),
        catchError(err => {
          console.warn('[OS] Falha ao cancelar orçamentos vinculados:', err);
          return of({ cancelled: 0, skipped: 0, total: 0 });
        })
      );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders(true) })
      .pipe(
        map(() => void 0),
        catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir ordem de serviço');
        return throwError(() => err);
        })
      );
  }

  // ===== Dashboard =====
  // GET /serviceorders/dashboard?configOrderId=...

  private mapDashboardStatusItem(dto: any): ServiceOrderDashboardStatusItem | null {
    const status = this.pickValue(dto, ['status', 'Status']);
    const count = this.toNumber(this.pickValue(dto, ['count', 'Count']));
    if (!status || count === undefined) {
      return null;
    }
    return { status: String(status), count };
  }

  private mapDashboardData(dto: any): ServiceOrderDashboardData {
    const rawByStatus = this.normalizeArray(this.pickValue(dto, ['byStatus', 'ByStatus']));
    const rangeDto = this.pickValue(dto, ['closedLastMonthRange', 'ClosedLastMonthRange']);

    return {
      configOrderIds: this.normalizeArray(this.pickValue(dto, ['configOrderIds', 'ConfigOrderIds']))
        .map(id => this.toNumber(id))
        .filter((id): id is number => id !== undefined),
      totalInPeriod: this.toNumber(this.pickValue(dto, ['totalInPeriod', 'TotalInPeriod'])) ?? 0,
      closedLastMonth: this.toNumber(this.pickValue(dto, ['closedLastMonth', 'ClosedLastMonth'])) ?? 0,
      closedLastMonthRange: rangeDto
        ? {
            startDate: this.pickValue(rangeDto, ['startDate', 'StartDate']),
            endDate: this.pickValue(rangeDto, ['endDate', 'EndDate'])
          }
        : undefined,
      byStatus: rawByStatus
        .map(item => this.mapDashboardStatusItem(item))
        .filter((item): item is ServiceOrderDashboardStatusItem => !!item)
    };
  }

  /** Obtém dados do dashboard de ordens de serviço (GET /serviceorders/dashboard). */
  getDashboard(configOrderIds: number[]): Observable<ServiceOrderDashboardData> {
    let params = new HttpParams();
    configOrderIds.forEach(id => {
      params = params.append('configOrderId', id.toString());
    });

    return this.http
      .get<any>(this.buildUrl(`${this.resourcePath}/dashboard`), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.mapDashboardData(this.extractItem(res))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao carregar dashboard de ordens de serviço');
          return throwError(() => err);
        })
      );
  }

  // ===== Exportação Excel =====
  // GET  /serviceorders/export/columns
  // POST /serviceorders/export

  /** Lista colunas disponíveis para exportação (GET /serviceorders/export/columns). */
  getExportColumns(): Observable<ServiceOrderExportColumn[]> {
    return this.http.get<any>(
      this.buildUrl(`${this.resourcePath}/export/columns`),
      { headers: this.getHeaders() }
    ).pipe(
      map(res => this.extractExportColumns(res)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao carregar colunas para exportação');
        return throwError(() => err);
      })
    );
  }

  /**
   * Exporta ordens de serviço para Excel (POST /serviceorders/export).
   */
  exportToExcel(body: ServiceOrderExportRequest): Observable<ServiceOrderExcelExportResult> {
    return this.http.post(
      this.buildUrl(`${this.resourcePath}/export`),
      this.buildExportPayload(body),
      {
        headers: this.getHeaders(),
        responseType: 'blob',
        observe: 'response'
      }
    ).pipe(
      map(response => this.mapExcelExportResponse(response)),
      catchError(err => {
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            try {
              const errorText = JSON.parse(reader.result as string);
              this.errorService.showErrorMessage(errorText.message || 'Erro ao gerar planilha Excel');
            } catch {
              this.errorService.showErrorMessage('Erro ao gerar planilha Excel');
            }
          };
          reader.readAsText(err.error);
        } else {
          this.errorService.showErrorMessage('Erro ao gerar planilha Excel');
        }
        return throwError(() => err);
      })
    );
  }
}

